import { appWithTranslation } from "next-i18next";
import Script from "next/script";
import { DefaultSeo } from "next-seo";
import "../pageStyles/site.scss";
import Head from "next/head";
import { AppContextProvider } from "@kunduz/feature-website-context";
import { useState } from "react";
import {
  Notifications,
  useNavigationProgress,
} from "@kunduz/feature-website-common";
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

export function reportWebVitals({ id, name, label, value }) {
  try {
    if (process.env.NEXT_PUBLIC_ENV_NAME !== "production") return;

    (window as any).gtag("event", name, {
      value: Math.round(name === "CLS" ? value * 1000 : value), // Use `delta`
      metric_id: id,
      metric_value: value,
    });
  } catch (error) {
    return null;
  }
}

function MyApp({ Component, pageProps }) {
  const [queryClient] = useState(() => new QueryClient());
  useNavigationProgress();

  const getLayout = Component.getLayout || ((page) => page);

  return (
    <QueryClientProvider client={queryClient} key="query-client-provider">
      <HydrationBoundary
        state={pageProps.dehydratedState}
        key="hydration-boundary"
      >
        <AppContextProvider lang={pageProps.lang} key="app-context-provider">
          <Head>
            <meta
              name="viewport"
              content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
            />
          </Head>

          <DefaultSeo
            title="Kunduz"
            titleTemplate="%s - Kunduz"
            additionalMetaTags={[
              {
                name: "referrer",
                content: "same-origin",
              },
            ]}
            openGraph={{
              type: "website",
              locale: "en_US",
              url: "https://kunduz.com/",
              site_name: "Kunduz",
              images: [
                {
                  url: "/icons/icon_192x192.png",
                  width: 192,
                  height: 192,
                },
              ],
            }}
            twitter={{
              handle: pageProps.lang == "en" ? "@askkunduz" : "@kunduzasor",
              cardType: "summary_large_image",
            }}
          />

          <Script id="tag-manager" strategy="afterInteractive">
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');`}
          </Script>

          <Script id="gtag" strategy="afterInteractive">
            {`window.dataLayer = window.dataLayer || [];
  function gtag(){ dataLayer.push({...arguments[0]}) };`}
          </Script>

          <Script id="segment" strategy="afterInteractive">
            {`!function(){var e=window.analytics=window.analytics||[];function t(e){(e||{}).writeKey=window.analyticsWriteKey,fetch("${process.env.NEXT_PUBLIC_ANALYTICS_URL}",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(e)}).catch((function(){}))}var n={name:"Kunduzlytics",type:"enrichment",version:"0.0.1",isLoaded:()=>!0,load:()=>Promise.resolve(),track:e=>(t(e.event),e.event={},e),identify:e=>(t(e.event),e.event={},e),page:e=>e,alias:e=>e,group:e=>e,screen:e=>e};if(!e.initialize)if(e.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{e.invoked=!0,e.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"],e.factory=function(t){return function(){var n=Array.prototype.slice.call(arguments);return n.unshift(t),e.push(n),e}};for(var i=0;i<e.methods.length;i++){var a=e.methods[i];e[a]=e.factory(a)}e.load=function(t){var n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src="/kunduzlytics.min.js";var i=document.getElementsByTagName("script")[0];i.parentNode.insertBefore(n,i),e._loadOptions=t},e.SNIPPET_VERSION="4.15.3",window.analyticsWriteKey="${process.env.NEXT_PUBLIC_ANALYTICS_KEY}",e.load(),e.register(n)}}();`}
          </Script>

          <Script id="service-worker">
            {`if ('serviceWorker' in navigator) {
              window.addEventListener('load', function() {
                navigator.serviceWorker.register('/sw.js');
              });
            }`}
          </Script>

          <Script id="prevent-pwa-banner">
            {`window.addEventListener("beforeinstallprompt", (event) => {
              event.preventDefault();
            });`}
          </Script>

          <Script id="inappbrowser-navbar-hide">
            {`try {
          if(navigator.userAgent && navigator.userAgent.indexOf("inappbrowser") > -1){
            document.querySelector("nav").style.display = "none";
            document.querySelector("footer").style.display = "none";
          }
        } catch (error) {}`}
          </Script>
          <Script id="window-lang">{`window.lang = '${pageProps.lang}'`}</Script>

          {getLayout(<Component {...pageProps} />)}

          <Notifications />
        </AppContextProvider>
      </HydrationBoundary>
      <ReactQueryDevtools buttonPosition="bottom-right" client={queryClient} />
    </QueryClientProvider>
  );
}

export default appWithTranslation(MyApp);
